import React from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Warning as WarningIcon,
  Description as DocIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

interface ChecklistItemDrawerProps {
  open: boolean;
  onClose: () => void;
  checkName: string;
  item: {
    itemId: string;
    status: string;
    detail?: string;
  } | null;
  reasoning?: string;
  citations?: Array<{
    documentName?: string;
    excerpt?: string;
    pageNumber?: number;
  }>;
  onCitationClick?: (citation: any) => void;
}

const STATUS_CONFIG: Record<
  string,
  { icon: React.ReactNode; label: string; color: string; bg: string }
> = {
  PASS: {
    icon: <PassIcon sx={{ fontSize: 22 }} />,
    label: "Pass",
    color: tokens.status.success,
    bg: `${tokens.status.success}10`,
  },
  FAIL: {
    icon: <FailIcon sx={{ fontSize: 22 }} />,
    label: "Fail",
    color: tokens.status.danger,
    bg: `${tokens.status.danger}10`,
  },
  WARNING: {
    icon: <WarningIcon sx={{ fontSize: 22 }} />,
    label: "Warning",
    color: tokens.status.warning,
    bg: `${tokens.status.warning}10`,
  },
};

const SECTION_LABEL_SX = {
  fontSize: tokens.fontSize["2xs"],
  fontWeight: tokens.fontWeight.semibold,
  color: tokens.text.muted,
  letterSpacing: "0.08em",
} as const;

const humanizeItemId = (itemId: string): string => {
  const stripped = itemId.replace(/^[a-z0-9]+_/i, "");
  return stripped
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const ChecklistItemDrawer: React.FC<ChecklistItemDrawerProps> = ({
  open,
  onClose,
  checkName,
  item,
  reasoning,
  citations,
  onCitationClick,
}) => {
  if (!item) return null;

  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.WARNING;
  const displayName = humanizeItemId(item.itemId);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${tokens.border.light}`,
          bgcolor: statusCfg.bg,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Box sx={{ color: statusCfg.color }}>{statusCfg.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.lg,
                  fontWeight: tokens.fontWeight.heavy,
                  color: tokens.text.primary,
                  lineHeight: 1.3,
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: tokens.fontSize["2xs"],
                  color: tokens.text.muted,
                  mt: 0.25,
                }}
              >
                {checkName}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Status badge */}
        <Box mt={1}>
          <Chip
            label={statusCfg.label}
            size="small"
            sx={{
              height: 22,
              fontSize: tokens.fontSize["2xs"],
              fontWeight: tokens.fontWeight.semibold,
              bgcolor: statusCfg.color,
              color: "#fff",
            }}
          />
        </Box>
      </Box>

      {/* Content — scrollable */}
      <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
        {/* Item Detail */}
        {item.detail && (
          <Box mb={3}>
            <Typography variant="overline" sx={SECTION_LABEL_SX}>
              Detail
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                color: tokens.text.primary,
                mt: 0.5,
                lineHeight: 1.6,
              }}
            >
              {item.detail}
            </Typography>
          </Box>
        )}

        {/* Reasoning */}
        {reasoning && (
          <Box mb={3}>
            <Typography variant="overline" sx={SECTION_LABEL_SX}>
              Analysis
            </Typography>
            <Box
              sx={{
                mt: 0.75,
                p: 2,
                bgcolor: "#f8f9fa",
                borderRadius: tokens.radius.md,
                borderLeft: `3px solid ${statusCfg.color}`,
                maxHeight: 360,
                overflow: "auto",
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  color: tokens.text.body,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.75,
                }}
              >
                {reasoning}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Citations */}
        {citations && citations.length > 0 && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <Box>
              <Typography variant="overline" sx={SECTION_LABEL_SX}>
                Evidence ({citations.length} citation
                {citations.length !== 1 ? "s" : ""})
              </Typography>
              <Stack spacing={1} mt={1}>
                {citations.map((citation, idx) => (
                  <Box
                    key={idx}
                    onClick={() => onCitationClick?.(citation)}
                    sx={{
                      p: 1.5,
                      borderRadius: tokens.radius.sm,
                      border: `1px solid ${tokens.border.light}`,
                      cursor: onCitationClick ? "pointer" : "default",
                      transition: "all 150ms ease",
                      "&:hover": onCitationClick
                        ? {
                            borderColor: tokens.brand.primary,
                            bgcolor: `${tokens.brand.primary}04`,
                          }
                        : {},
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <DocIcon
                        sx={{
                          fontSize: 16,
                          color: tokens.text.muted,
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {citation.documentName && (
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize.sm2,
                                fontWeight: tokens.fontWeight.semibold,
                                color: onCitationClick
                                  ? tokens.brand.primary
                                  : tokens.text.primary,
                              }}
                            >
                              {citation.documentName}
                            </Typography>
                          )}
                          {citation.pageNumber != null && (
                            <Chip
                              label={`p. ${citation.pageNumber}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "10px" }}
                            />
                          )}
                        </Stack>
                        {citation.excerpt && (
                          <Box
                            sx={{
                              mt: 0.75,
                              pl: 1.5,
                              borderLeft: `2px solid ${tokens.border.default}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize.xs,
                                color: tokens.text.excerpt,
                                fontStyle: "italic",
                                lineHeight: 1.5,
                              }}
                            >
                              &ldquo;{citation.excerpt}&rdquo;
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ChecklistItemDrawer;
